import {
  DateFormatEnum,
  DateFormatListing,
  IClassification,
  LanguageListing,
  RetentionPeriodListing,
  TranscriptTimerListing
} from "app/models/preferences.interface";

export const CLASSIFICATION_OPTIONS: IClassification[] = [
  {
    label: 'ICD',
    value: 'ICD',
    description: 'International Classification of Diseases'
  },
  {
    label: 'SNOMED',
    value: 'SNOMED',
    description: 'Systematized Nomenclature of Medicine'
  }
]

export enum ResourceType {
  SUBJECTIVE = 'Subjective',
  OBJECTIVE = 'Objective',
  ASSESSMENT = 'Assessment',
  PLAN = 'Plan'
}

export enum SubSectionTitle {
  CHIEF_COMPLAINTS = 'Chief Complaints',
  VITAL_SIGNS = 'Vital Signs',
  ASSESSMENT = 'Assessment',
  LAB_ORDER = 'Lab Order',
  PLAN = 'Plan',
  PHYSICAL_EXAM = 'Physical Exam',
  "PRESCRIPTION/MEDICATION" = 'Prescription/Medication',
  ALLERGY = 'Allergy',
  FAMILY_HISTORY = 'Family History',
  SOCIAL_HISTORY = 'Social History'
}

export enum SOAP_AI_Title {
  Symptoms = "Symptoms",
  Condition = "Condition",
  History_Of_Illness = "History_Of_Illness",
  Observations = "Observations",
  Diagnosis = "Diagnosis",
  Physical_Examination = "Physical_Examination",
  Lab_Report = "Lab_Report",
  Clinical_Impression = "Clinical_Impression",
  History_Of_Allergy = "History_Of_Allergy",
  Family_History = "Family_History",
  Social_History = "Social_History",
  Further_Plan = "Further_Plan",
  Lab_Order = "Lab_Order",
  Imaging_Order = "Imaging_Order",
  Recommended_Exercise = "Recommended_Exercise",
  Medication = "Medication",
  Immunization_Recommended = "Immunization_Recommended",
  Nutrition_Recommended = "Nutrition_Recommended",
  Referrals = "Referrals",
  Allergy_Addition = "Allergy_Addition",
  Vitals = "Vitals"
}

export enum TranscriptTimerEnum {
  NONE = "none",
  COUNT_UP_TIME = "countUpTime",
  SYSTEM_TIME = "systemTime"
}

export enum RetentionPeriodEnum {
  ONE_WEEK = '1_week',
  ONE_MONTH = '1_month',
  THREE_MONTHS = '3_months',
}

export enum LanguageEnum {
  EN_US = "en_us",
  EN_NZ = "en_nz",
  EN_AUS = "en_aus"
}

export enum ClassificationEnum {
  ICD = 'ICD',
  SNOMED = 'SNOMED'
}

export const retentionPeriodOptions: RetentionPeriodListing[] = [
  { id: RetentionPeriodEnum.ONE_WEEK, name: "1 Week" },
  { id: RetentionPeriodEnum.ONE_MONTH, name: "1 Month" },
  { id: RetentionPeriodEnum.THREE_MONTHS, name: "3 Months" },
];

export const DefaultPreferences = {
  "defaultNoteTemplate": 'none',
  "transcriptSettings": {
    "isSavingAllowed": false,
    "retentionPeriod": retentionPeriodOptions[2].id
  },
  "autoGenerateNotes": false,
  "showConsentPopUp": false,
  "language": LanguageEnum.EN_US,
  "snomedICDInference": "",
  "transcriptTimer": TranscriptTimerEnum.COUNT_UP_TIME,
  "dateFormat": DateFormatEnum.ISO,
  "classificationType": ClassificationEnum.ICD
}


export const languageListing: LanguageListing[] = [
  { id: LanguageEnum.EN_US, name: "English (US)", code: "US" },
  { id: LanguageEnum.EN_NZ, name: "English (NZ)", code: "NZ" },
  { id: LanguageEnum.EN_AUS, name: "English (AU)", code: "AU" }
]

export const dateFormatListing: DateFormatListing[] = [
  { id: DateFormatEnum.ISO, name: 'ISO Format', format: 'YYYY-MM-DD', example: '2024-12-11' },
  { id: DateFormatEnum.SHORT, name: 'Short Format', format: 'MM/DD/YYYY', example: '12/11/2024' },
  { id: DateFormatEnum.EUROPEAN, name: 'European Format', format: 'DD/MM/YYYY', example: '11/12/2024' },
  { id: DateFormatEnum.CUSTOM1, name: 'Custom 1', format: 'MM-DD-YYYY', example: '12-11-2024' },
  { id: DateFormatEnum.CUSTOM2, name: 'Custom 2', format: 'DD-MM-YYYY', example: '11-12-2024' },
  { id: DateFormatEnum.CUSTOM3, name: 'Custom 3', format: 'YYYY/MM/DD', example: '2024/12/11' },
  { id: DateFormatEnum.WITH_TIME, name: 'With Time', format: 'YYYY-MM-DD HH:mm:ss', example: '2024-12-11 15:30:45' },
  { id: DateFormatEnum.TIME_12H, name: '12-Hour Format', format: 'MM/DD/YYYY hh:mm A', example: '12/11/2024 03:30 PM' },
  { id: DateFormatEnum.TIME_24H, name: '24-Hour Format', format: 'DD/MM/YYYY HH:mm', example: '11/12/2024 15:30' },
  { id: DateFormatEnum.MONTH_ABBREV, name: 'Month Abbreviated', format: 'DD MMM YYYY', example: '11 Dec 2024' },
  { id: DateFormatEnum.MONTH_YEAR, name: 'Month and Year', format: 'MMMM YYYY', example: 'December 2024' },
];

export const transcriptTimerListing: TranscriptTimerListing[] = [
  { id: TranscriptTimerEnum.NONE, name: "None", example: '' },
  { id: TranscriptTimerEnum.COUNT_UP_TIME, name: "Count Up Timer", example: '00:10:25, format:HH:mm:ss' },
  { id: TranscriptTimerEnum.SYSTEM_TIME, name: "System Time", example: '12:45 PM, format: HH:mm A' }
]

export type GENERATED_SOAP_NOTE_MAPPING_Type = {
  [key in SubSectionTitle]: SOAP_AI_Title[];
}

export const GENERATED_SOAP_NOTE_MAPPING: GENERATED_SOAP_NOTE_MAPPING_Type = {
  [SubSectionTitle.CHIEF_COMPLAINTS]: [
    SOAP_AI_Title.Symptoms,
    SOAP_AI_Title.Condition,
    SOAP_AI_Title.History_Of_Illness
  ],
  [SubSectionTitle.VITAL_SIGNS]: [
    SOAP_AI_Title.Observations,
    SOAP_AI_Title.Vitals
  ],
  [SubSectionTitle.ASSESSMENT]: [
    SOAP_AI_Title.Diagnosis,
    SOAP_AI_Title.Clinical_Impression
  ],
  [SubSectionTitle.LAB_ORDER]: [
    SOAP_AI_Title.Lab_Order,
    SOAP_AI_Title.Lab_Report
  ],
  [SubSectionTitle.PLAN]: [
    SOAP_AI_Title.Further_Plan,
    SOAP_AI_Title.Recommended_Exercise,
    SOAP_AI_Title.Immunization_Recommended,
    SOAP_AI_Title.Nutrition_Recommended,
    SOAP_AI_Title.Referrals,
    SOAP_AI_Title.Imaging_Order
  ],
  [SubSectionTitle.PHYSICAL_EXAM]: [
    SOAP_AI_Title.Physical_Examination
  ],
  [SubSectionTitle["PRESCRIPTION/MEDICATION"]]: [
    SOAP_AI_Title.Medication
  ],
  [SubSectionTitle.ALLERGY]: [
    SOAP_AI_Title.History_Of_Allergy,
    SOAP_AI_Title.Allergy_Addition
  ],
  [SubSectionTitle.FAMILY_HISTORY]: [SOAP_AI_Title.Family_History],
  [SubSectionTitle.SOCIAL_HISTORY]: [SOAP_AI_Title.Social_History]
}

type DEFAULT_SOAP_NOTE_RESOURCES_MAPPING_TYPE = {
  [key in SubSectionTitle]: ResourceType;
}
export const DEFAULT_SOAP_NOTE_RESOURCES_MAPPING: DEFAULT_SOAP_NOTE_RESOURCES_MAPPING_TYPE = {
  [SubSectionTitle.CHIEF_COMPLAINTS]: ResourceType.SUBJECTIVE,
  [SubSectionTitle.VITAL_SIGNS]: ResourceType.OBJECTIVE,
  [SubSectionTitle.ASSESSMENT]: ResourceType.ASSESSMENT,
  [SubSectionTitle.LAB_ORDER]: ResourceType.OBJECTIVE,
  [SubSectionTitle.PLAN]: ResourceType.PLAN,
  [SubSectionTitle.PHYSICAL_EXAM]: ResourceType.ASSESSMENT,
  [SubSectionTitle["PRESCRIPTION/MEDICATION"]]: ResourceType.OBJECTIVE,
  [SubSectionTitle.ALLERGY]: ResourceType.SUBJECTIVE,
  [SubSectionTitle.FAMILY_HISTORY]: ResourceType.ASSESSMENT,
  [SubSectionTitle.SOCIAL_HISTORY]: ResourceType.ASSESSMENT
}

export const SOAP_MAPPING_WITH_RESOURCES: { [key in ResourceType]: SubSectionTitle[] } = {
  [ResourceType.SUBJECTIVE]: [
    SubSectionTitle.CHIEF_COMPLAINTS,
    SubSectionTitle.ALLERGY
  ],
  [ResourceType.OBJECTIVE]: [
    SubSectionTitle.VITAL_SIGNS,
    SubSectionTitle.LAB_ORDER,
    SubSectionTitle["PRESCRIPTION/MEDICATION"],
  ],
  [ResourceType.ASSESSMENT]: [
    SubSectionTitle.ASSESSMENT,
    SubSectionTitle.PHYSICAL_EXAM,
    SubSectionTitle.FAMILY_HISTORY,
    SubSectionTitle.SOCIAL_HISTORY
  ],
  [ResourceType.PLAN]: [
    SubSectionTitle.PLAN
  ]
};